import { useState, useEffect, useCallback, useRef } from "react";
import { useAdxQuery } from "../lib/useAdxQuery";
import { getPrompts, getPromptTrace, getActivityMeta } from "../lib/apiClient";
import { formatTime, splitEmail, RANGES, rangePhrase } from "../lib/format";
import PageHead from "../components/PageHead";
import { Icon } from "../components/Icon";
import { ErrorAlert, Loading, Empty, Field } from "../components/States";

// Has its own query state: sharing the list's `run` put the whole list into its
// loading state (and unmounted it) every time a trace was opened.
function TraceDialog({ promptId, onClose }) {
  const { run, loading, error } = useAdxQuery();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    run(getPromptTrace, promptId).then(d => {
      if (d) setRows(d);
      setLoaded(true);
    });
  }, [promptId]); // eslint-disable-line

  useEffect(() => {
    const opener = document.activeElement;
    closeRef.current?.focus();
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="dialog-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="trace-title">
        <div className="dialog__head">
          <h2 className="dialog__title" id="trace-title">Prompt trace</h2>
          <span className="dialog__meta">{promptId}</span>
        </div>
        <div className="dialog__body">
          <ErrorAlert message={error} />
          {loading || !loaded ? <Loading label="Loading trace…" />
            : error ? null
            : rows.length === 0 ? <Empty title="No trace events for this prompt." />
            : (
              <div className="grid-scroll">
                <table className="grid">
                  <thead>
                    <tr><th>Time</th><th>Event</th><th>Model</th><th className="num">In</th><th className="num">Out</th><th className="num">Cost</th><th className="num">ms</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.Timestamp}|${r.EventName}|${i}`}>
                        <td className="nowrap cell-muted">{new Date(r.Timestamp).toLocaleTimeString()}</td>
                        <td><span className="tag">{(r.EventName || "").replace("claude_code.", "")}</span></td>
                        <td className="cell-muted">{r.ModelId || "—"}</td>
                        <td className="num">{r.InputTokens != null ? r.InputTokens.toLocaleString() : "—"}</td>
                        <td className="num">{r.OutputTokens != null ? r.OutputTokens.toLocaleString() : "—"}</td>
                        <td className="num">{r.CostUsd != null ? `$${Number(r.CostUsd).toFixed(4)}` : "—"}</td>
                        <td className="num">{r.DurationMs ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
        <div className="dialog__foot">
          <button ref={closeRef} className="btn btn--secondary" type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function PromptRow({ prompt, onViewTrace }) {
  const [expanded, setExpanded] = useState(false);
  const text = prompt.PromptContent || "";
  const isLong = text.length > 300;
  const { local } = splitEmail(prompt.UserEmail || "unknown");
  const cost = prompt.TotalCostUsd ?? prompt.CostUsd;

  return (
    <div className="row row--top">
      <span className="row__icon"><Icon name="code" size={18} /></span>
      <div className="row__main">
        <p className="row__title">{local}</p>
        <p className="row__sub">{prompt.UserEmail || "—"} · {formatTime(prompt.Timestamp)}</p>
        <p className={`prompt__text${expanded || !isLong ? "" : " prompt__text--clamped"}`}>{text}</p>
        <div className="row__tags">
          {prompt.ModelId && <span className="tag tag--nav">{prompt.ModelId}</span>}
          {cost != null && <span className="tag">${Number(cost).toFixed(4)}</span>}
          {prompt.InputTokens ? <span className="tag">{Number(prompt.InputTokens).toLocaleString()} in</span> : null}
          {prompt.OutputTokens ? <span className="tag">{Number(prompt.OutputTokens).toLocaleString()} out</span> : null}
        </div>
      </div>
      <div className="row__actions">
        {isLong && (
          <button className="btn btn--ghost btn--sm" type="button" aria-expanded={expanded} onClick={() => setExpanded(e => !e)}>
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
        {prompt.PromptId && (
          <button className="btn btn--secondary btn--sm" type="button" onClick={() => onViewTrace(prompt.PromptId)}>
            <Icon name="list" size={16} /> View trace
          </button>
        )}
      </div>
    </div>
  );
}

const PAGE_SIZE = 50;

export default function PromptsView() {
  const { run, loading, error } = useAdxQuery();
  const [prompts, setPrompts] = useState([]);
  const [take, setTake] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [userEmail, setUser] = useState("");
  const [days, setDays] = useState("7");
  const [applied, setApplied] = useState(null);
  const [traceId, setTraceId] = useState(null);
  const [users, setUsers] = useState([]);

  const load = useCallback(async (newTake = PAGE_SIZE, f = { keyword, userEmail, days }) => {
    setTake(newTake);
    const data = await run(getPrompts, { keyword: f.keyword, userEmail: f.userEmail, days: parseInt(f.days), take: newTake + 1 });
    if (data) {
      setHasMore(data.length > newTake);
      setPrompts(data.slice(0, newTake));
      setApplied({ filtered: Boolean(f.keyword || f.userEmail), days: f.days });
    }
  }, [run, keyword, userEmail, days]);

  const loadMore = useCallback(() => {
    load(take + PAGE_SIZE);
  }, [load, take]);

  const clearFilters = () => {
    setKeyword("");
    setUser("");
    load(PAGE_SIZE, { keyword: "", userEmail: "", days });
  };

  const closeTrace = useCallback(() => setTraceId(null), []);

  useEffect(() => {
    load();
    run(getActivityMeta).then(d => { if (d) setUsers(d.users || []); });
  }, []); // eslint-disable-line

  const totalCost = prompts.reduce((s, p) => s + (Number(p.TotalCostUsd ?? p.CostUsd) || 0), 0);

  return (
    <>
      <PageHead
        viewId="prompts"
        description="User prompts from Claude Code and Cowork, captured through OpenTelemetry."
      />
      <div className="content">
        <ErrorAlert message={error} />
        <form className="card filterbar" onSubmit={e => { e.preventDefault(); load(); }}>
          <div className="card__body filters">
            <Field id="prompts-keyword" label="Keyword" grow>
              <input id="prompts-keyword" type="search" className="filterfield" placeholder="Search prompt content…"
                value={keyword} onChange={e => setKeyword(e.target.value)} />
            </Field>
            <Field id="prompts-user" label="User">
              <input id="prompts-user" type="text" className="filterfield" placeholder="user@eplp.com" list="prompts-users"
                autoComplete="off" value={userEmail} onChange={e => setUser(e.target.value)} />
              <datalist id="prompts-users">
                {users.map(u => <option key={u} value={u} />)}
              </datalist>
            </Field>
            <Field id="prompts-range" label="Time range">
              <select id="prompts-range" className="filterfield" value={days} onChange={e => setDays(e.target.value)}>
                {RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            <button className="btn btn--primary btn--sm" type="submit" disabled={loading}>
              <Icon name="search" size={16} /> Search
            </button>
          </div>
        </form>

        {loading || (!applied && !error) ? (
          <Loading label="Searching prompts…" />
        ) : prompts.length === 0 ? (
          error ? null : applied.filtered ? (
            <Empty
              title="No prompts match these filters."
              detail={`Nothing for that keyword or user in ${rangePhrase(applied.days)}.`}
              action={<button className="btn btn--secondary btn--sm" type="button" onClick={clearFilters}>Clear filters</button>}
            />
          ) : (
            <Empty title={`No prompts in ${rangePhrase(applied.days)}.`} />
          )
        ) : (
          <>
            <p className="summary">
              <strong>{prompts.length}{hasMore ? "+" : ""}</strong> prompt{prompts.length !== 1 ? "s" : ""}
              {totalCost > 0 && (
                <> · <strong>${totalCost >= 1 ? totalCost.toFixed(2) : totalCost.toFixed(4)}</strong> total cost{hasMore ? " (loaded results only)" : ""}</>
              )}
            </p>
            <div className="rows">
              {prompts.map(p => <PromptRow key={p.Id} prompt={p} onViewTrace={setTraceId} />)}
            </div>
            {hasMore && (
              <div className="loadmore">
                <button className="btn btn--secondary" type="button" onClick={loadMore} disabled={loading}>Load more</button>
              </div>
            )}
          </>
        )}
      </div>
      {traceId && <TraceDialog promptId={traceId} onClose={closeTrace} />}
    </>
  );
}
